/**
 * ModalLimpiezaPractica — cierre OBLIGATORIO del tutorial (KAN-232).
 *
 * Al terminar un módulo (o salir con la ✕) habiendo cargado algo durante la
 * corrida, este modal bloquea el centro de la pantalla hasta que el usuario
 * decida ítem por ítem: ¿eran datos reales (se quedan) o de práctica (se
 * borran)? No se puede cerrar con backdrop ni escapando: la única salida son
 * los dos botones. Mantiene el campo limpio de data de prueba.
 *
 * Montado por TutorialProvider (aparece sobre cualquier página).
 */

import React, { useMemo, useState } from 'react';
import { Button } from '../ui';
import ConfirmationModal from '../common/ConfirmationModal';
import { usePermissions } from '../../hooks/usePermissions';
import {
  borrarItemPractica,
  itemBloqueado,
  marcarResuelta,
  obtenerCorrida,
  quitarItems,
  type ItemPractica,
} from '../../services/tutorial/practicaTracker';

const EMOJI_TABLA: Record<ItemPractica['tabla'], string> = {
  animales: '🐄',
  gastos: '💸',
  ingresos: '💰',
  lluvias: '🌧️',
  recordatorios: '⏰',
  unidades: '🏡',
  ubicaciones: '📍',
  elementos_mapa: '🗺️',
  notas: '📝',
};

interface ModalLimpiezaPracticaProps {
  userId: string;
  moduleId: string;
  /** Título del módulo para el copy. */
  moduleTitle: string;
  /** Cierra el modal — SOLO lo llama el provider tras resolverse. */
  onClose: () => void;
}

export const ModalLimpiezaPractica: React.FC<ModalLimpiezaPracticaProps> = ({
  userId,
  moduleId,
  moduleTitle,
  onClose,
}) => {
  const { can } = usePermissions();
  const [version, setVersion] = useState(0);
  const [seleccion, setSeleccion] = useState<Record<string, boolean> | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [errores, setErrores] = useState<string[]>([]);

  const corrida = useMemo(
    () => obtenerCorrida(userId, moduleId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, moduleId, version],
  );

  // Sin ítems o ya resuelto (caso remoto): nada que decidir, se cierra solo.
  const sinPendiente = !corrida || corrida.items.length === 0 || Boolean(corrida.resuelta);
  React.useEffect(() => {
    if (sinPendiente) onClose();
  }, [sinPendiente, onClose]);
  if (sinPendiente) return null;

  const clave = (item: ItemPractica) => `${item.tabla}|${item.id}`;
  const bloqueado = (item: ItemPractica) =>
    itemBloqueado(item, permiso => can(permiso.module, permiso.action));
  const tildado = (item: ItemPractica) =>
    seleccion === null ? !bloqueado(item) : Boolean(seleccion[clave(item)]);
  const seleccionados = corrida.items.filter(i => tildado(i) && !bloqueado(i));

  const toggle = (item: ItemPractica) => {
    const k = clave(item);
    setSeleccion(prev => ({
      ...(prev ?? Object.fromEntries(corrida.items.map(i => [clave(i), !bloqueado(i)]))),
      [k]: !tildado(item),
    }));
  };

  const conservarTodo = () => {
    marcarResuelta(userId, moduleId, 'conservada');
    onClose();
  };

  const borrarTildados = async () => {
    setConfirmando(false);
    setBorrando(true);
    setErrores([]);
    const resultados = await Promise.allSettled(seleccionados.map(i => borrarItemPractica(i)));
    const fallidos: string[] = [];
    const exitosos: ItemPractica[] = [];
    resultados.forEach((r, idx) => {
      if (r.status === 'fulfilled') exitosos.push(seleccionados[idx]);
      else fallidos.push(seleccionados[idx].label);
    });
    if (exitosos.length > 0) quitarItems(userId, moduleId, exitosos);
    setErrores(fallidos);
    setSeleccion(null);
    setBorrando(false);
    setVersion(v => v + 1);
    const restante = obtenerCorrida(userId, moduleId);
    if (fallidos.length === 0 && (restante?.items.length ?? 0) === 0) {
      marcarResuelta(userId, moduleId, 'limpiada');
      onClose();
    }
    // Si quedaron ítems (fallidos o no tildados), el modal sigue abierto: el
    // usuario decide de nuevo sobre lo que quedó.
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Decidir qué hacer con lo cargado en el tutorial"
      data-testid="modal-limpieza-practica"
    >
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-paper p-6 shadow-overlay">
        <h3 className="font-display text-lg font-bold text-ink">
          🧹 ¿Qué hacemos con lo que cargaste en “{moduleTitle}”?
        </h3>
        <p className="mt-2 text-sm text-sage">
          Para mantener tu campo limpio, decidí si lo que agregaste durante este tutorial eran
          <strong> datos reales </strong>(querés conservarlos) o <strong>de práctica</strong>
          (querés borrarlos). Tildá lo que sea de práctica — lo desmarcado se queda.
        </p>

        <ul className="mt-4 space-y-1.5">
          {corrida.items.map(item => {
            const lock = bloqueado(item);
            return (
              <li
                key={clave(item)}
                className="flex items-center gap-3 rounded-md border border-hairline bg-paper-2/40 px-3 py-2"
                data-testid={`practica-item-${item.id}`}
              >
                <input
                  type="checkbox"
                  checked={tildado(item)}
                  disabled={lock || borrando}
                  onChange={() => toggle(item)}
                  className="h-4 w-4 accent-forest"
                  aria-label={`Borrar ${item.label}`}
                />
                <span className="text-base" aria-hidden="true">{EMOJI_TABLA[item.tabla]}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-ink">{item.label}</span>
                {lock && (
                  <span
                    className="shrink-0 text-xs text-sage"
                    title="Tu rol no puede borrar este elemento — pedile al dueño o admin."
                  >
                    🔒 requiere dueño/admin
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {errores.length > 0 && (
          <p className="mt-2 text-sm text-danger" role="alert">
            No se pudieron borrar: {errores.join(', ')}. Probá de nuevo en un rato.
          </p>
        )}

        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={conservarTodo} disabled={borrando} data-testid="practica-conservar">
            Eran datos reales — dejarlos
          </Button>
          <Button
            variant="primary"
            onClick={() => setConfirmando(true)}
            disabled={borrando || seleccionados.length === 0}
            data-testid="practica-borrar"
          >
            {borrando ? 'Borrando…' : `Borrar los tildados (${seleccionados.length})`}
          </Button>
        </div>
        <p className="mt-3 text-center text-xs text-sage">
          Elegí una opción para continuar — así tu campo queda limpio de datos de prueba.
        </p>
      </div>

      <ConfirmationModal
        isOpen={confirmando}
        onClose={() => setConfirmando(false)}
        onConfirm={borrarTildados}
        title="Borrar datos de práctica"
        message={`¿Borrar ${seleccionados.length} ${
          seleccionados.length === 1 ? 'elemento' : 'elementos'
        } de práctica? Esta acción se sincroniza con toda la empresa.`}
        confirmText="Sí, borrar"
        isDestructive
      />
    </div>
  );
};
